import { useQuery } from '@tanstack/react-query'
import request, { authOptions } from 'api/requestUtils'
import { definitions } from '@luxuryescapes/contract-svc-promo'
import { mapCompetitionConfig, mapCompetitionMinorPrize, mapCompetitionUserStatus, mapContentfulWebPageData } from './mapping'
import { DestinationLandingPageType } from 'components/Pages/DestinationLandingPage/DestinationLandingPageUtils'
import { paths } from '@luxuryescapes/contract-svc-public-offer-feed'
import qs from 'qs'
import config from 'constants/config'

export async function getReferralValue(region: string) {
  return request.get<App.ApiResponse<App.ReferralValue>>(`/api/referral/value?regionCode=${region}`)
}

export async function getReferralLogs(accessToken?: string) {
  return request.get<App.ApiResponse<App.ReferralLogs>>('/api/referral/logs', authOptions(accessToken))
}

export async function getEarnOptions(region: string) {
  return request.get<App.ApiResponse<{ earn_options: Array<App.EarnOption> }>>(`/api/referral/earn-options?region=${region}`)
}

export async function getBestBackPrize() {
  return request.get('/api/promo/competition/bestOfAllTime/entry', { credentials: 'include' })
}

export async function getBestBackObject() {
  return request.get('/api/promo/competition/bestOfAllTime/config')
}

export async function getBestBackUserStatus(accessToken?: string) {
  return request.get('/api/promo/competition/bestOfAllTime/check', authOptions(accessToken))
}

export async function getNblCompetitionPrize() {
  return request.get('/api/promo/competition/nblCompetition/entry', { credentials: 'include' })
}

export async function getNblCompetitionObject() {
  return request.get('/api/promo/competition/nblCompetition/config')
}

export async function getNblCompetitionUserStatus(accessToken?: string) {
  return request.get('/api/promo/competition/nblCompetition/check?', authOptions(accessToken))
}

export async function getAuCompetitionPrize() {
  return request.get('/api/promo/competition/auCompetition/entry?', { credentials: 'include' })
}

export async function getAuCompetitionObject() {
  return request.get('/api/promo/competition/auCompetition/config')
}

export async function getAuCompetitionUserStatus(region?: string, accessToken?: string) {
  return request.get(`/api/promo/competition/auCompetition/check?region=${region}`, authOptions(accessToken))
}

export async function getUkCompetitionPrize() {
  return request.get('/api/promo/competition/ukCompetition/entry', { credentials: 'include' })
}

export async function getUkCompetitionObject() {
  return request.get('/api/promo/competition/ukCompetition/config')
}

export async function getUkCompetitionUserStatus(accessToken?: string) {
  return request.get('/api/promo/competition/ukCompetition/check', authOptions(accessToken))
}

export async function getIntlCompetitionPrize(region?: string) {
  return request.get(`/api/promo/competition/intlCompetition/entry?region=${region}`, { credentials: 'include' })
}

export async function getIntlCompetitionObject(region?: string) {
  return request.get(`/api/promo/competition/intlCompetition/config?region=${region}`)
}

export async function getIntlCompetitionUserStatus(region?: string, accessToken?: string) {
  return request.get(`/api/promo/competition/intlCompetition/check?region=${region}`, authOptions(accessToken))
}

export async function getCompetitionPrize(region?: string, competitionName?: string) {
  return request.get(`/api/promo/competition/${competitionName}/entry?region=${region}`, { credentials: 'include' })
}

export async function getCompetitionObject(region?: string, competitionName?: string) {
  return request.get(`/api/promo/competition/${competitionName}/config?region=${region}`)
}

export async function getCompetitionUserStatus(region?: string, competitionName?: string, accessToken?: string) {
  return request.get(`/api/promo/competition/${competitionName}/check?region=${region}`, authOptions(accessToken))
}

export async function getDestinationPage(destination: string) {
  return request.get(`/api/content/pageDestinationDiscover/${destination}`)
}

export async function getContentfulWebPage(path: string, regionCode: string) {
  return request.get(`/api/content/web-page?slug=${path}&regionCode=${regionCode}`)
}

type GetForcedRankingOffersResponse = paths['/api/public-offer-feed/forced-rankings-offer']['get']['responses']['200']['content']['application/json']['result']
export async function getForcedRankingOffers(region: string, latitude: number, longitude: number, offerCountry: string, limit = 5): Promise<App.ApiResponse<GetForcedRankingOffersResponse>> {
  return request.get(`/api/public-offer-feed/forced-rankings-offer?country_code=${offerCountry}&latitude=${latitude}&longitude=${longitude}&user_region=${region}&limit=${limit}`, { excludeBrand: true })
}

type GetMetaSearchOffersResponse = paths['/api/public-offer-feed/meta-offers']['get']['responses']['200']['content']['application/json']['result']
export async function getMetaSearchOffers(region: string, latitude: number, longitude: number, offerCountry: string, limit = 5, checkIn?: string): Promise<App.ApiResponse<GetMetaSearchOffersResponse>> {
  const queryString = qs.stringify({
    country_code: offerCountry,
    latitude: latitude.toString(),
    longitude: longitude.toString(),
    user_region: region,
    forced_rankings_limit: limit.toString(),
    checkIn,
  })
  return request.get(`/api/public-offer-feed/meta-offers?${queryString}`, { credentials: 'include' })
}

export type GetMetaOffersDiscountParams = Omit<paths['/api/public-offer-feed/offer-discount/']['get']['parameters']['query'], 'brand'>
type GetMetaOffersDiscountResponse = paths['/api/public-offer-feed/offer-discount/']['get']['responses']['200']['content']['application/json']['result']
export async function getOfferDiscount(params: GetMetaOffersDiscountParams) {
  return request.get<App.ApiResponse<GetMetaOffersDiscountResponse>>(`/api/public-offer-feed/offer-discount?${qs.stringify(params)}`, { credentials: 'include' })
    .then((response) => {
      return response.result
    })
}

export function generalLog({ status, message, payload }: Omit<paths['/api/public-offer-feed/log']['post']['requestBody']['content']['application/json'], 'brand'>) {
  return request.post('/api/public-offer-feed/log', {
    status,
    message,
    payload,
  })
}

type GetPlaceSiteLinksResponse = paths['/api/public-offer-feed/v1/sitelink/{pageType}/{placeName}']['get']['responses']['200']['content']['application/json']
export async function getPlaceSitelinks(
  { pageType, placeName }: { pageType: DestinationLandingPageType, placeName: string }): Promise<GetPlaceSiteLinksResponse> {
  return request.get(`/api/public-offer-feed/v1/sitelink/${pageType}/${placeName.toLowerCase()}`)
    .then((response) => {
      return response as GetPlaceSiteLinksResponse
    })
}

export function useCompetitionConfig(slug: string, region?: string) {
  return useQuery<any, { errors: Array<string> }>({
    queryKey: [`competition-config-${slug}`],
    queryFn: () => getCompetitionObject(region, slug),
    select: (response: { result: definitions['CompetitionConfigV2'] }): App.MarketingCompetitionConfig => {
      return mapCompetitionConfig(response.result)
    },
  })
}

export function useCompetitionPrize(competitionName?: string, episodeId = 1, region?: string) {
  return useQuery({
    queryKey: [`competition-prize-${competitionName}-${episodeId}`],
    queryFn: () => getCompetitionPrize(region, competitionName),
    enabled: false,
    select: (response: any): App.MarketingCompetitionMinorPrize => {
      return mapCompetitionMinorPrize(response)
    },
  })
}

export function useCompetitionStatus(competitionName?: string, episode = 1, region?: string) {
  return useQuery({
    queryKey: [`competition-status-${competitionName}-${episode}`],
    queryFn: () => getCompetitionUserStatus(region, competitionName),
    select: (response: any): App.MarketingCompetitionUserStatus => {
      return mapCompetitionUserStatus(response)
    },
  })
}

export function useContentfulWebPage(path: string, regionCode: string) {
  return useQuery({
    queryKey: ['contentful-web-page', path],
    queryFn: () => getContentfulWebPage(path, regionCode),
    select: (response: any): App.ContentfulWebPageData => {
      return mapContentfulWebPageData(response.result[0].fields)
    },
  })
}

export function useFetchMetaOffersDiscount(params: {
  offerId: string
  region: string
  checkIn: string
  checkOut: string
  occupancy: string
  enabled?: boolean
}) {
  const { offerId, region, checkIn, checkOut, occupancy, enabled = true } = params

  // Check for meta offers cookie
  const hasMetaOffersCookie = typeof document !== 'undefined' && document?.cookie?.split('; ').some(cookie => cookie.startsWith('metaofferSID'))

  return useQuery({
    queryKey: ['meta-offers-discount', offerId, region, checkIn, checkOut, occupancy],
    queryFn: () => getOfferDiscount({
      offerId,
      region,
      checkIn,
      checkOut,
      occupancy,
    }),
    enabled: enabled && !!offerId && !!checkIn && !!checkOut && !!occupancy && hasMetaOffersCookie && config.ENABLE_META_OFFERS_DISCOUNT,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export type ValidateOfferDiscountParams = Omit<paths['/api/public-offer-feed/offer-discount/validate']['get']['parameters']['query'], 'brand'>
type ValidateOfferDiscountResponse = paths['/api/public-offer-feed/offer-discount/validate']['get']['responses']['200']['content']['application/json']
export async function validateOfferDiscount(params: ValidateOfferDiscountParams): Promise<string> {
  return request.get<ValidateOfferDiscountResponse>(`/api/public-offer-feed/offer-discount/validate?${qs.stringify(params)}`, { credentials: 'include' })
    .then((response) => {
      return response.result.promoCode
    })
}
